import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Clock,
  CreditCard,
  Bell,
  Edit2,
  CheckCircle,
  DollarSign,
  Mail,
  Phone,
  FileText
} from 'lucide-react';
import { useConfig } from '../context/ConfigContext';
import { useReceivableQuery, useSendReceivableReminder } from '../hooks/useQueries';
import { useToast } from '../context/ToastContext';

const AVATAR_COLORS = [
  'bg-violet-500', 'bg-pink-500', 'bg-amber-500', 'bg-emerald-500',
  'bg-blue-500', 'bg-rose-500', 'bg-teal-500', 'bg-orange-500'
];
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
const getAvatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  partial: { label: 'Partial', color: 'bg-blue-100 text-blue-700', icon: DollarSign },
  settled: { label: 'Settled', color: 'bg-green-100 text-green-700', icon: CheckCircle }
};

export default function ReceivableDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { formatAmount, formatDate, config } = useConfig();
  const { toast } = useToast();

  const { data: receivableData = {}, isLoading } = useReceivableQuery(id, { enabled: !!id });
  const sendReminderMutation = useSendReceivableReminder();

  const receivable = receivableData.receivable;
  const payments = receivableData.payments || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!receivable) {
    return (
      <div className="text-center py-12">
        <p className="text-surface-600">Receivable not found</p>
        <button
          onClick={() => navigate('/receivables')}
          className="mt-4 btn-primary"
        >
          Go Back
        </button>
      </div>
    );
  }

  const remaining = (receivable.amount || 0) - (receivable.paidAmount || 0);
  const isOverdue = receivable.dueDate && new Date(receivable.dueDate) < new Date() && receivable.status !== 'settled';
  const StatusIcon = STATUS_CONFIG[receivable.status]?.icon || Clock;

  const handleSendReminder = async () => {
    try {
      await sendReminderMutation.mutateAsync(receivable._id);
      toast.success('Reminder sent to contact!');
    } catch (error) {
      toast.error(error.error || 'Failed to send reminder');
    }
  };

  return (
    <>
      {/* Mobile: Full-screen layout */}
      <div className="xl:hidden fixed inset-0 bg-white z-50 flex flex-col" style={{ paddingBottom: '72px' }}>
        {/* Mobile Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-surface-200 bg-white flex-shrink-0">
          <button
            onClick={() => navigate('/receivables')}
            className="p-2 -ml-2 text-surface-600 hover:text-surface-900 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${getAvatarColor(receivable.contact?.name)}`}>
              {getInitials(receivable.contact?.name)}
            </div>
            <div>
              <h3 className="font-semibold text-surface-900">
                {receivable.contact?.name || 'Unknown'}
              </h3>
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${STATUS_CONFIG[receivable.status]?.color}`}>
                <StatusIcon className="w-2.5 h-2.5" />
                {STATUS_CONFIG[receivable.status]?.label}
              </span>
            </div>
          </div>
        </div>

        {/* Mobile Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Amount Summary */}
          <div className="bg-surface-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-surface-600">Total Amount</span>
              <span className="text-lg font-bold text-surface-900">
                {formatAmount(receivable.amount || 0)}
              </span>
            </div>
            {receivable.paidAmount > 0 && (
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-surface-600">Received</span>
                <span className="text-sm font-medium text-green-600">
                  - {formatAmount(receivable.paidAmount || 0)}
                </span>
              </div>
            )}
            <div className="border-t border-surface-200 pt-3 flex items-center justify-between">
              <span className="text-sm font-medium text-surface-700">Remaining</span>
              <span className="text-lg font-bold" style={{ color: remaining > 0 ? config.incomeColor : '#64748b' }}>
                {formatAmount(remaining)}
              </span>
            </div>
            {/* Progress Bar */}
            {receivable.amount > 0 && (
              <div className="mt-3">
                <div className="h-2 bg-surface-200 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${Math.min(100, ((receivable.paidAmount || 0) / receivable.amount) * 100)}%`,
                      backgroundColor: config.incomeColor
                    }}
                  />
                </div>
                <p className="text-xs text-surface-500 mt-1 text-right">
                  {Math.round(((receivable.paidAmount || 0) / receivable.amount) * 100)}% received
                </p>
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="text-xs text-surface-500 mb-1 block">Description</label>
            <p className="text-sm text-surface-900">{receivable.description}</p>
          </div>

          {/* Dates */}
          <div className="bg-surface-50 rounded-xl p-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-surface-500 mb-1 block">Lent Date</label>
                <div className="flex items-center gap-1.5 text-sm text-surface-700">
                  <Calendar className="w-4 h-4 text-surface-400" />
                  {formatDate(receivable.lentDate)}
                </div>
              </div>
              <div>
                <label className="text-xs text-surface-500 mb-1 block">Due Date</label>
                <div className={`flex items-center gap-1.5 text-sm ${isOverdue ? 'text-red-600' : 'text-surface-700'}`}>
                  <Calendar className="w-4 h-4 text-surface-400" />
                  {receivable.dueDate ? formatDate(receivable.dueDate) : '-'}
                  {isOverdue && <span className="text-xs text-red-500">(Overdue)</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          {(receivable.contact?.email || receivable.contact?.mobile) && (
            <div className="bg-surface-50 rounded-xl p-3">
              <label className="text-xs text-surface-500 mb-2 block">Contact Info</label>
              <div className="space-y-1.5">
                {receivable.contact?.email && (
                  <div className="flex items-center gap-2 text-sm text-surface-700">
                    <Mail className="w-4 h-4 text-surface-400" />
                    {receivable.contact.email}
                  </div>
                )}
                {receivable.contact?.mobile && (
                  <div className="flex items-center gap-2 text-sm text-surface-700">
                    <Phone className="w-4 h-4 text-surface-400" />
                    {receivable.contact.mobile}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {receivable.notes && (
            <div>
              <label className="text-xs text-surface-500 mb-1 block">Notes</label>
              <div className="flex items-start gap-2 text-sm text-surface-700 bg-surface-50 p-3 rounded-xl">
                <FileText className="w-4 h-4 text-surface-400 mt-0.5 flex-shrink-0" />
                {receivable.notes}
              </div>
            </div>
          )}

          {/* Payment History */}
          {payments.length > 0 && (
            <div>
              <label className="text-xs text-surface-500 mb-2 block">
                Payment History ({payments.length})
              </label>
              <div className="space-y-2">
                {payments.map((payment) => (
                  <div
                    key={payment._id}
                    className="bg-green-50 rounded-lg p-3 flex items-center justify-between"
                  >
                    <div>
                      <p className="text-sm font-medium text-green-700">
                        {formatAmount(payment.amount)}
                      </p>
                      <p className="text-xs text-green-600">
                        {formatDate(payment.paymentDate)}
                        {payment.paymentMethod && ` • ${payment.paymentMethod.replace('_', ' ')}`}
                      </p>
                    </div>
                    {payment.notes && (
                      <p className="text-xs text-green-600 truncate max-w-[120px]">
                        {payment.notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        {receivable.status !== 'settled' && receivable.status !== 'cancelled' && (
          <div className="p-4 border-t border-surface-100 space-y-2 flex-shrink-0 bg-white">
            {/* Primary Action */}
            <button
              onClick={() => navigate(`/receivables/${receivable._id}/payment`)}
              className="w-full flex items-center justify-center gap-1.5 py-2.5 px-3 bg-primary-50 text-primary-700 rounded-xl font-medium text-sm hover:bg-primary-100 transition-colors"
            >
              <CreditCard className="w-4 h-4 flex-shrink-0" />
              Record Payment
            </button>
            {/* Secondary Actions Row */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={handleSendReminder}
                disabled={sendReminderMutation.isPending || !receivable.contact?.email}
                className="flex items-center justify-center gap-1.5 py-2 px-3 border border-surface-200 text-surface-700 rounded-xl text-sm hover:bg-surface-50 transition-colors disabled:opacity-50"
              >
                <Bell className="w-4 h-4 flex-shrink-0" />
                Send Reminder
              </button>
              <button
                onClick={() => navigate(`/receivables/${receivable._id}/edit`)}
                className="flex items-center justify-center gap-1.5 py-2 px-3 border border-surface-200 text-surface-700 rounded-xl text-sm hover:bg-surface-50 transition-colors"
              >
                <Edit2 className="w-4 h-4 flex-shrink-0" />
                Edit
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Desktop: Card layout */}
      <div className="hidden xl:block">
        <div className="max-w-2xl mx-auto">
          {/* Desktop Header */}
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate('/receivables')}
              className="p-2 text-surface-500 hover:text-surface-700 hover:bg-surface-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 flex-1">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-base ${getAvatarColor(receivable.contact?.name)}`}>
                {getInitials(receivable.contact?.name)}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-surface-900">{receivable.contact?.name || 'Unknown'}</h1>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CONFIG[receivable.status]?.color}`}>
                  <StatusIcon className="w-3 h-3" />
                  {STATUS_CONFIG[receivable.status]?.label}
                </span>
              </div>
            </div>
          </div>

          {/* Desktop Content Card */}
          <div className="bg-white rounded-2xl border border-surface-200 p-6 space-y-6">
            {/* Amount Summary */}
            <div className="bg-surface-50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-surface-600">Total Amount</span>
                <span className="text-xl font-bold text-surface-900">
                  {formatAmount(receivable.amount || 0)}
                </span>
              </div>
              {receivable.paidAmount > 0 && (
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-surface-600">Received</span>
                  <span className="text-sm font-medium text-green-600">
                    - {formatAmount(receivable.paidAmount || 0)}
                  </span>
                </div>
              )}
              <div className="border-t border-surface-200 pt-3 flex items-center justify-between">
                <span className="text-sm font-medium text-surface-700">Remaining</span>
                <span className="text-xl font-bold" style={{ color: remaining > 0 ? config.incomeColor : '#64748b' }}>
                  {formatAmount(remaining)}
                </span>
              </div>
              {/* Progress Bar */}
              {receivable.amount > 0 && (
                <div className="mt-3">
                  <div className="h-2 bg-surface-200 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${Math.min(100, ((receivable.paidAmount || 0) / receivable.amount) * 100)}%`,
                        backgroundColor: config.incomeColor
                      }}
                    />
                  </div>
                  <p className="text-xs text-surface-500 mt-1 text-right">
                    {Math.round(((receivable.paidAmount || 0) / receivable.amount) * 100)}% received
                  </p>
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="text-xs text-surface-500 mb-1 block">Description</label>
              <p className="text-surface-900">{receivable.description}</p>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-surface-500 mb-1 block">Lent Date</label>
                <div className="flex items-center gap-1.5 text-surface-700">
                  <Calendar className="w-4 h-4 text-surface-400" />
                  {formatDate(receivable.lentDate)}
                </div>
              </div>
              <div>
                <label className="text-xs text-surface-500 mb-1 block">Due Date</label>
                <div className={`flex items-center gap-1.5 ${isOverdue ? 'text-red-600' : 'text-surface-700'}`}>
                  <Calendar className="w-4 h-4 text-surface-400" />
                  {receivable.dueDate ? formatDate(receivable.dueDate) : '-'}
                  {isOverdue && <span className="text-xs text-red-500">(Overdue)</span>}
                </div>
              </div>
            </div>

            {/* Contact Info */}
            {(receivable.contact?.email || receivable.contact?.mobile) && (
              <div className="bg-surface-50 rounded-xl p-4">
                <label className="text-xs text-surface-500 mb-2 block">Contact Info</label>
                <div className="flex flex-wrap gap-4">
                  {receivable.contact?.email && (
                    <div className="flex items-center gap-2 text-surface-700">
                      <Mail className="w-4 h-4 text-surface-400" />
                      {receivable.contact.email}
                    </div>
                  )}
                  {receivable.contact?.mobile && (
                    <div className="flex items-center gap-2 text-surface-700">
                      <Phone className="w-4 h-4 text-surface-400" />
                      {receivable.contact.mobile}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Notes */}
            {receivable.notes && (
              <div>
                <label className="text-xs text-surface-500 mb-1 block">Notes</label>
                <div className="flex items-start gap-2 text-surface-700 bg-surface-50 p-3 rounded-xl">
                  <FileText className="w-4 h-4 text-surface-400 mt-0.5 flex-shrink-0" />
                  {receivable.notes}
                </div>
              </div>
            )}

            {/* Payment History */}
            {payments.length > 0 && (
              <div>
                <label className="text-xs text-surface-500 mb-2 block">
                  Payment History ({payments.length})
                </label>
                <div className="space-y-2">
                  {payments.map((payment) => (
                    <div
                      key={payment._id}
                      className="bg-green-50 rounded-lg p-3 flex items-center justify-between"
                    >
                      <div>
                        <p className="font-medium text-green-700">
                          {formatAmount(payment.amount)}
                        </p>
                        <p className="text-sm text-green-600">
                          {formatDate(payment.paymentDate)}
                          {payment.paymentMethod && ` • ${payment.paymentMethod.replace('_', ' ')}`}
                        </p>
                      </div>
                      {payment.notes && (
                        <p className="text-sm text-green-600 truncate max-w-[200px]">
                          {payment.notes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            {receivable.status !== 'settled' && receivable.status !== 'cancelled' && (
              <div className="pt-4 border-t border-surface-200 flex gap-3">
                <button
                  onClick={() => navigate(`/receivables/${receivable._id}/payment`)}
                  className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-colors"
                >
                  <CreditCard className="w-5 h-5" />
                  Record Payment
                </button>
                <button
                  onClick={handleSendReminder}
                  disabled={sendReminderMutation.isPending || !receivable.contact?.email}
                  className="flex items-center justify-center gap-2 py-3 px-4 border border-surface-200 text-surface-700 rounded-xl hover:bg-surface-50 transition-colors disabled:opacity-50"
                >
                  <Bell className="w-5 h-5" />
                  Remind
                </button>
                <button
                  onClick={() => navigate(`/receivables/${receivable._id}/edit`)}
                  className="flex items-center justify-center gap-2 py-3 px-4 border border-surface-200 text-surface-700 rounded-xl hover:bg-surface-50 transition-colors"
                >
                  <Edit2 className="w-5 h-5" />
                  Edit
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
